import axios, { AxiosRequestConfig } from "axios";

import getErrorDetails from "./helpers/getErrorDetails";
import paramsSerializer from "./helpers/paramsSerializer";
import Customer from "../models/Customer";
import { UUID } from "../utils/uuid";
import { addPaginationAndSort, ORDER_DESC } from "./helpers/pagination";
import CustomerPreferencesSettings from "../models/CustomerPreferencesBranding.js";
import { CustomerLogoType, CustomerLogoFile } from "../models/CustomerLogo";

export async function getCustomers(
  options?: AxiosRequestConfig
): Promise<{ list: Customer[] }> {
  const requestParams = addPaginationAndSort<Customer>({
    orderBy: "createdAt",
    order: ORDER_DESC,
    // TODO
    // remove after implementing pagination
    limit: 999,
    page: 0,
  });
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/customers",
      params: requestParams,
      paramsSerializer,
    });
    const { data = [] } = response;

    return {
      list: data,
    };
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getCustomer(
  id: UUID,
  options?: AxiosRequestConfig
): Promise<Customer> {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/customers/${id}`,
    });
    const { data = {} } = response;

    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function postCustomer(
  customer: Customer & { customer: UUID },
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: `/customers`,
      data: customer,
    });
    const { data = {} } = response;
    return await getCustomer(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function generateCustomerCredentials(
  id: UUID,
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: `/customers/${id}/credentials`,
    });
    const { data = {} } = response;
    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function regenerateCustomerCredentials(
  id: UUID,
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: `/customers/${id}/credentials/regenerate`,
    });
    const { data = {} } = response;
    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function patchCustomer(
  id: UUID,
  customer: Customer,
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "PUT",
      url: `/customers/${id}`,
      data: customer,
    });
    const { data = {} } = response;
    return await getCustomer(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function deleteCustomer(id: UUID, options?: AxiosRequestConfig) {
  try {
    await axios({
      ...options,
      method: "DELETE",
      url: `/customers/${id}`,
    });
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getCustomerBanksByCountry(
  id: UUID,
  options?: AxiosRequestConfig
): Promise<string[]> {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/customers/${id}/banks/countries`,
    });
    const { data = [] }: { data: any[] } = response;
    return data.map((country) => country.id);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function patchCustomerBanksByCountry(
  id: UUID,
  countries: string[],
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "PUT",
      url: `/customers/${id}/countries/enable-banks`,
      data: countries,
    });
    const { data = {} } = response;
    return data[0];
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getCustomerBanksCountByCountry(
  id: UUID,
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/customers/${id}/countries/banks-count`,
    });
    const { data = {} } = response;
    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function getCustomerPreferencesBranding(
  id: UUID,
  options?: AxiosRequestConfig
) {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/customers/${id}/preferences/branding`,
    });
    const { data = {} } = response;
    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function putCustomerPreferencesBranding(
  id: UUID,
  customerPreferences: CustomerPreferencesSettings,
  options?: AxiosRequestConfig
) {
  try {
    await axios({
      ...options,
      method: "PUT",
      url: `/customers/${id}/preferences/branding`,
      data: customerPreferences,
    });
    return await getCustomerPreferencesBranding(id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function postCustomerLogo(
  id: UUID,
  {
    logoType,
    file,
  }: {
    logoType: CustomerLogoType;
    file: CustomerLogoFile;
  },
  options?: AxiosRequestConfig
) {
  try {
    await axios({
      ...options,
      method: "POST",
      url: `/customers/${id}/preferences/branding/logo/${logoType}`,
      data: file,
      headers: {
        ...options?.headers,
        "Content-Type": file.type,
      },
    });
    return await getCustomerPreferencesBranding(id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}

export async function deleteCustomerLogo(
  id: UUID,
  {
    logoType,
  }: {
    logoType: CustomerLogoType;
  },
  options?: AxiosRequestConfig
) {
  try {
    await axios({
      ...options,
      method: "DELETE",
      url: `/customers/${id}/preferences/branding/logo/${logoType}`,
    });
    return await getCustomerPreferencesBranding(id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
}
